import { config } from '@config/config';
import { isIpac } from '@config/constants';
import * as monitoring from '@core/monitoring';
import type { Formation, Theme } from '@core/theme';
import type { Result } from '@ef-core/result';
import axios from 'axios';

export interface LeadPayload {
  situationPro?: string;
  professionalSituationName?: string | null;
  objectif?: string;
  domainId?: number;
  domainName?: string | null;
  idFormationBitrix?: number;
  formationId?: number;
  etudes?: string;
  financement?: string;
  formationMode?: string;
  hasRequestedAdvice?: number;
  sourceId?: string | null;
  sourceName?: string | null;
  sourceOriginLead?: string;
  email?: string;
  gender?: string;
  prenom?: string;
  nom?: string;
  numInt?: string;
  consentContact?: string;
  consentPartners?: string;

  origin?: string;
  metaEventId?: string;
  gclid?: string;
  ga_cid?: string;
  GA_CID?: string;
  session_anonyme?: string;
  SESSION_ANONYME?: string;
  fb_fbp?: string;
  fbclid?: string;
  utm_id?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;

  iss?: string;
  aud?: string;
}

interface SaveResponse {
  id?: number;
  result?: string;
}

const getToken = async (payload: LeadPayload): Promise<string> => {
  const response = await axios.post(
    `${config.cloudFunctionUrl.formLeadNew}?token-request=1`,
    JSON.stringify(payload),
    { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } },
  );
  return response.data;
};

export const save = async (
  payload: LeadPayload,
): Promise<Result<SaveResponse>> => {
  try {
    const token = await getToken(payload);
    const response = await axios.post<SaveResponse>(
      `${config.cloudFunctionUrl.formLeadNew}`,
      JSON.stringify(payload),
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return { success: true, data: response.data };
  } catch (error) {
    monitoring.reportError(error);
    return { success: false };
  }
};

const toBoolean = (value: unknown): boolean =>
  value === true || value === 'true';

interface RawFormation {
  id: number | string;
  id_formation_bitrix: number | string;
  name: string;
  commercial_name: string;
  duration: string;
  formation_type: Formation['formation_type'];
  order: number | string;
  is_default: boolean | string;
  has_cpf: boolean | string;
  is_eligible_alternance: boolean | string;
}

interface RawTheme {
  id: number | string;
  name: string;
  order: number | string;
  is_visible_in_form_lead: boolean | string;
  formations?: RawFormation[];
}

export const getThemes = async (): Promise<Result<Theme[]>> => {
  try {
    const { data } = await axios.get<{ docs?: RawTheme[] }>(
      `${config.apiUrl.leadFlow}/thematiques?max_results=999&site_url=${isIpac ? 'ipac' : 'ef'}`,
      { headers: { Accept: 'application/json' } },
    );

    return {
      success: true,
      data: (data.docs ?? [])
        .sort((a, b) => Number(a.order) - Number(b.order))
        .map((row) => ({
          id: Number(row.id),
          name: row.name,
          order: Number(row.order),
          is_visible_in_form_lead: toBoolean(row.is_visible_in_form_lead),
          formations: (row.formations || []).map((f) => ({
            id: Number(f.id),
            id_formation_bitrix: Number(f.id_formation_bitrix),
            name: f.name,
            commercial_name: f.commercial_name,
            duration: f.duration,
            formation_type: f.formation_type,
            order: Number(f.order),
            is_default: toBoolean(f.is_default),
            has_cpf: toBoolean(f.has_cpf),
            is_eligible_alternance: toBoolean(f.is_eligible_alternance),
          })),
        })),
    };
  } catch (e) {
    monitoring.reportError(e);
    return { success: false };
  }
};
